import React, { ComponentType, FC } from 'react'

import theme from 'styles/theme'
import { Text } from 'components/Box'
import { getDisplayName } from 'utils'
import styled from 'styled-components'

export interface HeadingProps {
  size?: keyof typeof headingTextStyles
}

export const headingTextStyles = {
  hero: {
    fontSize: '2.375rem',
    lineHeight: '3rem',
    fontWeight: theme.fontWeights.bold,
  },
  large: {
    fontSize: theme.fontSizes.xlarge,
    lineHeight: '2.25rem',
    fontWeight: theme.fontWeights.bold,
  },
  medium: {
    fontSize: theme.fontSizes.large,
    lineHeight: '2rem',
    fontWeight: theme.fontWeights.medium,
  },
  small: {
    fontSize: theme.fontSizes.body,
    lineHeight: '1.5rem',
    fontWeight: theme.fontWeights.medium,
  },
  smaller: {
    fontSize: theme.fontSizes.small,
    lineHeight: '1.25rem',
    fontWeight: theme.fontWeights.medium,
  },
  tiny: {
    fontSize: theme.fontSizes.smaller,
    lineHeight: '1.25rem',
    fontWeight: theme.fontWeights.medium,
  },
}

function withHeading<Props>(
  Comp: ComponentType<Props>,
  defaultSize: keyof typeof headingTextStyles
) {
  const Heading: FC<Props & HeadingProps> = ({ size, ...rest }) => {
    const props = {
      ...headingTextStyles[size || defaultSize],
      ...rest,
    } as Props
    return <Comp {...props} />
  }

  Heading.displayName = `withHeading(${getDisplayName(Comp)})`
  return styled(Heading)``
}

const H0 = withHeading(Text.H1, 'hero')
const H1 = withHeading(Text.H1, 'large')
const H2 = withHeading(Text.H2, 'medium')
const H3 = styled(withHeading(Text.H3, 'small'))`
  margin-top: 48px;
  margin-bottom: 48px;
`
const H4 = withHeading(Text.H4, 'smaller')
const H5 = withHeading(Text.H5, 'tiny')
const H6 = withHeading(Text.H6, 'tiny')

const Heading = {
  H0,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
}

export default Heading
